/**
 * SSO for the desktop shell, run in the user's system browser.
 *
 * In a browser this app signs in with SSO by navigating the tab to Platform's
 * `/o/authorize`, and the refresh token comes back as an HttpOnly cookie. The
 * desktop shell does it differently. The authorize URL opens in the system
 * browser, where the user already has an identity provider session and a
 * password manager. The OS then returns the code to the app through its custom
 * scheme, and `DesktopIntegration` routes that link to `/auth/callback` like
 * any other.
 *
 * That return path decides the client. A custom-scheme `redirect_uri` is
 * registered only on `wone-mobile`, a first-party public client whose tokens
 * come back in the response body. So the refresh token is this app's to keep,
 * and it lives in the shell's keychain-backed `secureStorage` under one key.
 *
 * Nothing here runs without the bridge. `auth-tokens.ts` calls in only when
 * `getDesktopBridge()` is non-null, or when the stored PKCE state says the
 * flow was started here.
 */

import type { WoneDesktopBridge } from "./bridge";
import { randomUrlSafe, sha256B64Url } from "providers/pkce";

export const DESKTOP_SSO_CLIENT_ID = "wone-mobile";

/** The single key the shell's `secureStorage` accepts. */
export const DESKTOP_SSO_REFRESH_KEY = "wone_desktop_sso_refresh";

/**
 * Mirrors `frontend/mobile/src/services/auth/ssoClient.ts`, since both ask
 * Platform for the same `wone-mobile` client. Every scope must be registered
 * in Platform's `OAUTH2_PROVIDER.SCOPES`.
 */
export const DESKTOP_SSO_SCOPES = [
  "openid",
  "profile",
  "email",
  "learner",
  "ori:chat",
  "feature-flags:write",
  "content:search",
  "knowledge:read",
  "readings:read",
  "readings:raw",
].join(" ");

/**
 * The PKCE session stored in sessionStorage while the system browser is open.
 * It shares the key the browser flow uses, so `/auth/callback` and the
 * per-provider analytics read it unchanged, and `client` is what tells the
 * two flows apart on return.
 */
export interface DesktopPkceState {
  client: typeof DESKTOP_SSO_CLIENT_ID;
  code_verifier: string;
  state: string;
  redirect_uri: string;
  return_to?: string;
  provider?: string;
}

export class DesktopSsoError extends Error {
  code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = "DesktopSsoError";
    this.code = code;
  }
}

/**
 * The redirect URI for this build, matched to the scheme the shell registers
 * with the OS. Development borrows staging's, because Platform has no local
 * scheme registered and the shell registers staging's for development too.
 * It must also match the exchange exactly, which is why it is stored with the
 * PKCE state rather than recomputed there.
 */
export function desktopRedirectUri(env: WoneDesktopBridge["env"]): string {
  return env === "production"
    ? "walkingonearth://oauth-callback"
    : "walkingonearth-staging://oauth-callback";
}

export function buildDesktopAuthorizeUrl(args: {
  platformOrigin: string;
  redirectUri: string;
  state: string;
  codeChallenge: string;
  idpHint: string;
  loginHint?: string;
}): string {
  const params = new URLSearchParams({
    client_id: DESKTOP_SSO_CLIENT_ID,
    response_type: "code",
    redirect_uri: args.redirectUri,
    scope: DESKTOP_SSO_SCOPES,
    state: args.state,
    code_challenge: args.codeChallenge,
    code_challenge_method: "S256",
    idp_hint: args.idpHint,
  });
  if (args.loginHint) params.set("login_hint", args.loginHint);
  return `${args.platformOrigin}/o/authorize/?${params.toString()}`;
}

export function isDesktopPkce(stored: unknown): stored is DesktopPkceState {
  return (
    typeof stored === "object" &&
    stored !== null &&
    (stored as { client?: unknown }).client === DESKTOP_SSO_CLIENT_ID
  );
}

/** Why a callback link was ignored. */
export type DesktopCallbackRejection =
  | "no_sign_in_in_progress"
  | "state_mismatch";

/**
 * What the user sees when a sign-in link is ignored while a sign-in of theirs
 * is in progress. A link that arrives with none in progress is ignored
 * without a message, since the user did not start anything.
 */
export const DESKTOP_CALLBACK_MESSAGES: Record<
  Exclude<DesktopCallbackRejection, "no_sign_in_in_progress">,
  string
> = {
  state_mismatch:
    "That sign-in link does not match the sign-in in progress, so it was ignored. Please try again.",
};

/**
 * Why a callback link the OS delivered does not belong to the sign-in this
 * app started, or `null` when it does. Checked before the app navigates to it.
 *
 * Any web page can fire a custom-scheme link, so a callback is untrusted until
 * its `state` matches the stored PKCE session. Checking here rather than only
 * in `/auth/callback` matters because that page clears local storage before it
 * runs the exchange: a stray link would otherwise disturb a signed-in user.
 * An `error` callback (the user cancelled at the identity provider) is held to
 * the same rule, so a forged one cannot end a real sign-in either.
 */
export function checkDesktopCallback(
  route: string,
  storedPkce: string | null
): DesktopCallbackRejection | null {
  const query = route.includes("?") ? route.slice(route.indexOf("?") + 1) : "";
  const state = new URLSearchParams(query).get("state");
  let pkce: unknown = null;
  if (storedPkce !== null) {
    try {
      pkce = JSON.parse(storedPkce);
    } catch {
      // Unparseable session state is no session at all; the user starts again.
      pkce = null;
    }
  }
  if (!isDesktopPkce(pkce)) return "no_sign_in_in_progress";
  if (state === null || state !== pkce.state) return "state_mismatch";
  return null;
}

/**
 * Starts a sign-in in the system browser. Resolves once the browser has been
 * asked to open; the result arrives later as a deep link.
 */
export async function beginDesktopSSO(args: {
  bridge: WoneDesktopBridge;
  platformOrigin: string;
  pkceSessionKey: string;
  idpHint: string;
  returnTo?: string;
  loginHint?: string;
}): Promise<void> {
  const codeVerifier = randomUrlSafe(32);
  const state = randomUrlSafe(16);
  const codeChallenge = await sha256B64Url(codeVerifier);
  const redirectUri = desktopRedirectUri(args.bridge.env);

  const pkce: DesktopPkceState = {
    client: DESKTOP_SSO_CLIENT_ID,
    code_verifier: codeVerifier,
    state,
    redirect_uri: redirectUri,
    return_to: args.returnTo,
    provider: args.idpHint,
  };
  sessionStorage.setItem(args.pkceSessionKey, JSON.stringify(pkce));

  const url = buildDesktopAuthorizeUrl({
    platformOrigin: args.platformOrigin,
    redirectUri,
    state,
    codeChallenge,
    idpHint: args.idpHint,
    loginHint: args.loginHint,
  });
  // The client and redirect are the two values worth checking when a sign-in
  // does not come back; state, challenge and hints stay out of the log.
  // eslint-disable-next-line no-console
  console.info(
    `[desktop-sso] opening the system browser for ${args.platformOrigin}/o/authorize/ ` +
      `client_id=${DESKTOP_SSO_CLIENT_ID} redirect_uri=${redirectUri}`
  );
  await args.bridge.openExternal(url);
}

type TokenBody = {
  access_token?: unknown;
  refresh_token?: unknown;
};

/**
 * Stores a refresh token, throwing if it cannot. Platform rotates refresh
 * tokens, so a rotation that is not persisted leaves only the consumed token
 * on disk, and the next launch presents a token Platform has already retired.
 */
async function persistRefreshToken(
  bridge: WoneDesktopBridge,
  token: string
): Promise<void> {
  if (bridge.secureStorage === undefined) {
    throw new DesktopSsoError(
      "this desktop shell has no secure storage for the refresh token",
      "refresh_persist_failed"
    );
  }
  try {
    await bridge.secureStorage.set(DESKTOP_SSO_REFRESH_KEY, token);
  } catch (err) {
    throw new DesktopSsoError(
      `could not store the refresh token: ${(err as Error).message}`,
      "refresh_persist_failed"
    );
  }
}

/**
 * Exchanges the code a deep link delivered for tokens, and stores the refresh
 * token before handing back the access token. The caller has already checked
 * `state`.
 */
export async function exchangeDesktopCode(args: {
  bridge: WoneDesktopBridge;
  platformOrigin: string;
  pkce: DesktopPkceState;
  code: string;
  fetchImpl?: (input: string, init: RequestInit) => Promise<Response>;
}): Promise<{ accessToken: string }> {
  const doFetch = args.fetchImpl ?? ((input, init) => fetch(input, init));
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code: args.code,
    client_id: DESKTOP_SSO_CLIENT_ID,
    redirect_uri: args.pkce.redirect_uri,
    code_verifier: args.pkce.code_verifier,
  });
  const res = await doFetch(`${args.platformOrigin}/o/token/`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    credentials: "omit",
    body: body.toString(),
  });
  if (!res.ok) {
    // Status only. The body of a token-endpoint error names the grant, and
    // this message reaches Sentry and the sign-in screen.
    throw new DesktopSsoError(
      `desktop SSO token exchange failed: ${res.status}`,
      "token_exchange_failed"
    );
  }
  const data = (await res.json()) as TokenBody;
  if (
    typeof data.access_token !== "string" ||
    typeof data.refresh_token !== "string"
  ) {
    throw new DesktopSsoError(
      "desktop SSO token response is missing access_token or refresh_token",
      "token_response_malformed"
    );
  }
  await persistRefreshToken(args.bridge, data.refresh_token);
  return { accessToken: data.access_token };
}

/** Same shape as `RefreshOutcome` in `auth-tokens.ts`, which re-exports it. */
export type DesktopRefreshOutcome =
  | { kind: "ok"; token: string }
  | { kind: "terminal" }
  | { kind: "keep" }
  | { kind: "transient" };

/**
 * One refresh with the stored `wone-mobile` refresh token, or `null` when
 * there is none to use (no bridge, an older shell, nothing stored).
 *
 * `terminal` means Platform rejected the token for good. The key is cleared
 * then, so the caller can fall back to the cookie path, which answers for
 * itself. A failure to reach Platform is `transient`, never a fall-back: a
 * missing cookie would turn a network blip into a sign-out.
 */
export async function attemptDesktopRefresh(args: {
  bridge: WoneDesktopBridge | null;
  platformOrigin: string;
  fetchImpl?: (input: string, init: RequestInit) => Promise<Response>;
}): Promise<DesktopRefreshOutcome | null> {
  const { bridge } = args;
  if (bridge === null || bridge.secureStorage === undefined) return null;
  const doFetch = args.fetchImpl ?? ((input, init) => fetch(input, init));

  let stored: string | null;
  try {
    stored = await bridge.secureStorage.get(DESKTOP_SSO_REFRESH_KEY);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      `[desktop-sso] could not read the stored refresh token; using the cookie path: ${
        (err as Error).message
      }`
    );
    return null;
  }
  if (stored === null || stored === "") return null;

  let res: Response;
  try {
    res = await doFetch(`${args.platformOrigin}/o/token/`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      credentials: "omit",
      body: new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: stored,
        client_id: DESKTOP_SSO_CLIENT_ID,
      }).toString(),
    });
  } catch {
    return { kind: "transient" };
  }

  if (res.status === 400 || res.status === 401) {
    // eslint-disable-next-line no-console
    console.warn(
      `[desktop-sso] Platform rejected the stored refresh token (${res.status}); clearing it`
    );
    try {
      await clearStoredRefreshToken(bridge);
    } catch {
      // Already logged. Platform has rejected this token for good, so a copy
      // left behind can only be rejected again; the cookie path still has to
      // run, or a dead keychain entry would block every sign-in after it.
    }
    return { kind: "terminal" };
  }
  if (res.status >= 500) return { kind: "transient" };
  if (!res.ok) {
    // eslint-disable-next-line no-console
    console.warn(
      `[desktop-sso] unexpected ${res.status} refreshing; keeping the session`
    );
    return { kind: "keep" };
  }

  const data = (await res.json()) as TokenBody;
  if (typeof data.access_token !== "string") {
    // eslint-disable-next-line no-console
    console.warn(
      "[desktop-sso] refresh response had no access_token; keeping the session"
    );
    return { kind: "keep" };
  }
  if (typeof data.refresh_token === "string" && data.refresh_token !== stored) {
    try {
      await persistRefreshToken(bridge, data.refresh_token);
    } catch (err) {
      // Not complete until it is stored. Reporting transient makes the caller
      // retry with the old token, which Platform answers with the same
      // successor inside its grace window, so the next attempt can store it.
      // eslint-disable-next-line no-console
      console.warn(
        `[desktop-sso] ${(err as Error).message}; will retry the refresh`
      );
      return { kind: "transient" };
    }
  }
  return { kind: "ok", token: data.access_token };
}

/**
 * Removes the stored refresh token, and fails loudly when it cannot. A token
 * left behind outlives the session it belonged to: the boot refresh tries the
 * keychain before the cookie, so it would sign that user back in, even after
 * someone else has signed in on this machine.
 */
async function clearStoredRefreshToken(
  bridge: WoneDesktopBridge
): Promise<void> {
  try {
    await bridge.secureStorage?.remove(DESKTOP_SSO_REFRESH_KEY);
  } catch (err) {
    const message = `could not clear the stored refresh token: ${
      (err as Error).message
    }`;
    // eslint-disable-next-line no-console
    console.warn(`[desktop-sso] ${message}`);
    throw new DesktopSsoError(message, "refresh_token_clear_failed");
  }
}

/**
 * Drops any desktop SSO session when the learner signs in another way. An
 * OTP or password sign-in sets the cookie, but the boot refresh prefers the
 * keychain, so a stored token from an earlier SSO sign-in would quietly swap
 * the session back to whoever that was.
 */
export async function forgetDesktopSsoSession(
  bridge: WoneDesktopBridge | null
): Promise<void> {
  if (bridge === null || bridge.secureStorage === undefined) return;
  await clearStoredRefreshToken(bridge);
}

/**
 * Revokes the stored refresh token at Platform and clears it. The key is
 * cleared whether or not the revoke succeeds, because a sign-out the user
 * asked for must not leave the app able to sign itself back in. A failure to
 * clear it is thrown, so the caller cannot report a clean sign-out.
 */
export async function signOutDesktopSession(args: {
  bridge: WoneDesktopBridge | null;
  platformOrigin: string;
}): Promise<void> {
  const { bridge } = args;
  if (bridge === null || bridge.secureStorage === undefined) return;

  let stored: string | null = null;
  try {
    stored = await bridge.secureStorage.get(DESKTOP_SSO_REFRESH_KEY);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      `[desktop-sso] could not read the refresh token to revoke it: ${
        (err as Error).message
      }`
    );
  }
  if (stored) {
    try {
      const res = await fetch(`${args.platformOrigin}/o/revoke_token/`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "omit",
        body: new URLSearchParams({
          token: stored,
          token_type_hint: "refresh_token",
          client_id: DESKTOP_SSO_CLIENT_ID,
        }).toString(),
      });
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.warn(
          `[desktop-sso] refresh token revoke answered ${res.status}`
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        `[desktop-sso] refresh token revoke failed: ${(err as Error).message}`
      );
    }
  }
  await clearStoredRefreshToken(bridge);
}
